import React, { useEffect, useState, useCallback } from "react";
import TableContainer from "../../Component/TableContainer";
import { Box, Checkbox, IconButton, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { masterAPI } from "../Masters/dataConfig";
import { axiosPrivate } from "../../axios/axios";
import { Check, Close } from "@mui/icons-material";
import { color } from "../../StyleData/Config";
import { encode } from "../../Component/JWT/Formatt";
import { toast } from "react-toastify";
import moment from "moment";
import UnifiedModal from "../../Component/UnifiedModal";
import { fromUTC } from "../../utils/dateUtil";

const WorkFlowApproval = () => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filterValue, setFilterValue] = useState("enableData");
  const [backUpData, setBackUpData] = useState([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectDescription, setRejectDescription] = useState("");
  const [rejectId, setRejectId] = useState(null);

  const getData = useCallback(async () => {
    setIsLoading(true);
    const link = masterAPI["workFlowApproval"].link;
    await axiosPrivate
      .get(`${link}`)
      .then((res) => {
        console.log(res.data.result, "workflow approval");

        // Sort by created date (newest first)
        const sortedData = res.data.result.sort((a, b) => {
          return new Date(b.createdDate) - new Date(a.createdDate);
        });

        if (filterValue === "enableData") {
          const filterDataResult = sortedData.filter((fil) => fil.status.toUpperCase() === "PENDING");
          setData(filterDataResult);
          setBackUpData(sortedData);
        } else {
          setData(sortedData);
          setBackUpData(sortedData);
        }
        setIsLoading(false);
      })
      .catch((e) => {
        console.log(e, "workflow approval error");
        setIsLoading(false);
        setData([]);
        setBackUpData([]);
        toast.error("Failed to fetch approval requests");
      });
  }, [filterValue]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleApprove = async (id) => {
    const updateLink = masterAPI["workFlowApproval"].update;
    const payload = {
      id: id,
      status: "Approved",
    };

    await axiosPrivate
      .put(`${updateLink}`, payload)
      .then((res) => {
        console.log(res.data);
        toast.success("Request approved successfully");
        getData();
      })
      .catch((e) => {
        console.log(e, "approve error");
        toast.error(
          e.response?.data?.failureMessage || "Failed to approve request",
        );
      });
  };

  const handleOpenRejectModal = (id) => {
    setRejectId(id);
    setRejectDescription("");
    setShowRejectModal(true);
  };

  const handleCloseRejectModal = () => {
    setShowRejectModal(false);
    setRejectDescription("");
    setRejectId(null);
  };

  const handleConfirmReject = async () => {
    if (!rejectDescription.trim()) {
      toast.error("Description is required to reject.");
      return;
    }
    await handleReject(rejectId, rejectDescription);
    handleCloseRejectModal();
  };

  const handleReject = async (id, description) => {
    const updateLink = masterAPI["workFlowApproval"].update;
    const payload = {
      id: id,
      status: "Rejected",
      description: description || undefined,
    };

    await axiosPrivate
      .put(`${updateLink}`, payload)
      .then((res) => {
        console.log(res.data);
        toast.success("Request rejected successfully");
        getData();
      })
      .catch((e) => {
        console.log(e, "reject error");
        toast.error(
          e.response?.data?.failureMessage || "Failed to reject request",
        );
      });
  };

  const handleCodeClick = (row) => {
    const workFlowFor = row.workFlowFor;

    // Route based on workFlowFor type
    switch (workFlowFor) {
      case "ServiceRequest":
        if (row.serviceRequestId) {
          navigate(
            `/masters/servicerequest?id=${encode(row.serviceRequestId)}&fromApproval=true&approvalId=${encode(row.id)}`,
          );
        }
        break;
      case "WorkOrderCreated":
        if (row.workOrderId) {
          // WorkOrder has a drilldown page, so navigate to the view page
          navigate(
            `/workorderview?id=${encode(row.workOrderId)}&fromApproval=true&approvalId=${encode(row.id)}`,
          );
        }
        break;
      case "WorkOrderClose":
        if (row.workOrderId) {
          // WorkOrder has a drilldown page, so navigate to the view page
          navigate(
            `/workorderview?id=${encode(row.workOrderId)}&fromApproval=true&approvalId=${encode(row.id)}`,
          );
        }
        break;
      case "PM":
        if (row.pmId) {
          navigate(
            `/preventivemaintenance?id=${encode(row.pmId)}&fromApproval=true&approvalId=${encode(row.id)}`,
          );
        }
        break;
      case "PartsReservation":
        if (row.workOrderId) {
          navigate(
            `/workorderview?id=${encode(row.workOrderId)}&fromApproval=true&approvalId=${encode(row.id)}`,
          );
        }
        break;
      default:
        console.log("Unknown workflow type:", workFlowFor);
    }
  };

  const getRequestCode = (row) => {
    // Return the appropriate code based on workFlowFor
    switch (row.workFlowFor) {
      case "ServiceRequest":
        return row.serviceRequestCode || row.serviceRequestName || "N/A";
      case "WorkOrderCreated":
        return row.workOrderCode || row.workOrderName || "N/A";
      case "WorkOrderClose":
        return row.workOrderCode || row.workOrderName || "N/A";
      case "PM":
        return row.pmCode || row.pmName || "N/A";
      case "PartsReservation":
        return row.workOrderCode || row.workOrderName || "N/A";
      default:
        return "N/A";
    }
  };

  const columns = [
    {
      accessor: "id",
      disableFilters: true,
      Cell: () => {
        return (
          <Box sx={{ width: color.checkBoxWidth }}>
            <Checkbox />
          </Box>
        );
      },
    },
    // {
    //   Header: "Request Code",
    //   accessor: "requestCode",
    //   Cell: ({ cell }) => {
    //     const requestCode = getRequestCode(cell.row.original);
    //     return (
    //       <span
    //         onClick={() => handleCodeClick(cell.row.original)}
    //         style={{
    //           color: "#0d6efd",
    //           textDecorationLine: "underline",
    //           fontWeight: 800,
    //           cursor: "pointer",
    //         }}
    //       >
    //         {requestCode}
    //       </span>
    //     );
    //   },
    // },
    {
      Header: "Request Code",
      accessor: (row) => getRequestCode(row), // ⭐ searchable value
      Cell: ({ value, row }) => (
        <span
          onClick={() => handleCodeClick(row.original)}
          style={{
            color: "#0d6efd",
            textDecorationLine: "underline",
            fontWeight: 800,
            cursor: "pointer",
          }}
        >
          {value}
        </span>
      ),
    },
    {
      Header: "Name",
      accessor: "personName",
      Cell: ({ cell }) => {
        return cell.value || "N/A";
      },
    },
    {
      Header: "WorkFlow Type",
      accessor: "workFlowFor",
      Cell: ({ cell }) => {
        return cell.value || "N/A";
      },
    },
    {
      Header: "WorkFlow Levels",
      accessor: "workFlowLevel",
      Cell: ({ cell }) => {
        return cell.value || "N/A";
      },
    },
    {
      Header: "Created At",
      accessor: "createdDate",
      Cell: ({ cell }) => {
        const createdDate = cell.row.original.createdDate;

        return createdDate && createdDate !== "0001-01-01T00:00:00"
          ? fromUTC(createdDate).tzString
          : "N/A";
      },
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ cell }) => {
        const status = cell.value || "N/A";
        const statusColor =
          status === "Approved"
            ? "green"
            : status === "Rejected"
              ? "red"
              : status === "Pending"
                ? "orange"
                : "default";

        return (
          <span style={{ color: statusColor, fontWeight: 600 }}>{status}</span>
        );
      },
    },
    {
      Header: "Action",
      Cell: ({ cell }) => {
        const status = cell.row.original.status;
        const isPending = status === "Pending";

        // Only show buttons if status is Pending
        if (!isPending) {
          return <span style={{ color: "gray", fontSize: "12px" }}>-</span>;
        }

        return (
          <Box sx={{ whiteSpace: "nowrap" }}>
            <IconButton
              onClick={() => handleApprove(cell.row.original.id)}
              title="Approve"
            >
              <Check
                sx={{
                  fontSize: color.masterEditDelButton,
                  color: "green",
                }}
              />
            </IconButton>
            <IconButton
              onClick={() => handleOpenRejectModal(cell.row.original.id)}
              title="Reject"
            >
              <Close
                sx={{
                  fontSize: color.masterEditDelButton,
                  color: "red",
                }}
              />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const handleChangeFilter = (e) => {
    if (e === "showAll") {
      setData(backUpData);
    } else {
      const filterDataResult = backUpData.filter((fil) => fil.isActive);
      setData(filterDataResult);
    }
    setFilterValue(e);
  };

  return (
    <>
      <Box>
        <Typography
          sx={{
            color: color.masterPageTitleColor,
            fontSize: color.masterPageTitleFontSize,
            fontWeight: color.masterPageTitleFontWeight,
            mb: color.masterPageTitlemd,
          }}
        >
          {masterAPI["workFlowApproval"].title}
        </Typography>
      </Box>

      <TableContainer
        columns={columns}
        data={data}
        loading={isLoading}
        buttonDisable={true}
        filterValue={filterValue}
        handleChange={handleChangeFilter}
        filterShow={true}
        buttonNew={false}
      />

      {/* Reject Remark Modal */}
      <UnifiedModal
        open={showRejectModal}
        onClose={handleCloseRejectModal}
        title="Reject Request"
        description={
          <span>
            Description <span style={{ color: "red" }}>*</span>
          </span>
        }
        inputLabel="Enter reason for rejection"
        inputValue={rejectDescription}
        onInputChange={(e) => setRejectDescription(e.target.value)}
        onCancel={handleCloseRejectModal}
        onReject={handleConfirmReject}
        rejectButtonColor="error"
        rejectButtonVariant="contained"
        minRows={3}
        autoFocus={true}
      />
    </>
  );
};

export default WorkFlowApproval;
